import { useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { UserInfo } from "../types/api/UserInfo";
import { getAllSources, getIsOnpremise, getPlatform, getUserName, isWebView } from ".";
import constants from "./constants";
import { get, isEmpty } from "lodash";
import { MIXPANEL_EVENTS, MIXPANEL_OTHER_EVENTS } from "./mixpanelEvents";
import Store from "./Store";
import getEnv from "./getEnv";
import Countly from "countly-sdk-web";
import getConfig from "next/config";
import isMobile from "./isMobile";

const PROD = "production";
const LOCALSTORAGE = "localStorage";
const isProduction = getEnv("NEXT_PUBLIC_APP_ENV") === PROD;

function getUserProperties(userData: UserInfo): Record<string, any> {
  const sources = getAllSources(userData);
  const sourceIds = sources.map((source) => source.uuid);
  const user = get(userData, ["details"], null);
  const PLATFORMS = constants.PLATFORMS;
  const platform = Object.keys(PLATFORMS).find(
    (p) => PLATFORMS[p as keyof typeof PLATFORMS] === getPlatform()
  );

  return {
    Name: getUserName(user),
    Email: user?.email,
    sourceIdentifier: user?.source_identifier || "personal_user",
    country: user?.country,
    vertical: user?.vertical,
    clientName: user?.client_name,
    sourceIds,
    isMobileApp: isWebView(),
    platform,
  };
}

function getCountlyFormattedUserProperties(userData: UserInfo): Record<string, any> {
  const sources = getAllSources(userData);
  const sourceIds = sources.map((source) => source.uuid);
  const PLATFORMS = constants.PLATFORMS;
  const platform = Object.keys(PLATFORMS).find(
    (p) => PLATFORMS[p as keyof typeof PLATFORMS] === getPlatform()
  );
  const appVersion = getConfig()?.publicRuntimeConfig?.version || "unknown";
  return {
    name: userData?.details?.name,
    username: userData?.details?.email,
    email: userData?.details?.email,
    app_version: appVersion,
    custom: {
      sourceIds,
      isMobileApp: isWebView(),
      platform,
      sourceIdentifier: userData?.details?.source_identifier || "personal_user",
      country: userData?.details?.country,
      vertical: userData?.details?.vertical,
      clientName: userData?.details?.client_name,
      appVersion,
      deviceName: getEnv("NEXT_PUBLIC_DEVICE_NAME") || "unknown",
    },
  };
}

const isLoggedInUser = () => {
  const userInfo = (window as any).userInfo as UserInfo;
  return get(userInfo, ["authenticated"], false);
};

function initMixpanel() {
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;
  const isOnPremise = getIsOnpremise();

  if (isOnPremise || !MIXPANEL_TOKEN) {
    return () => {};
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: !isProduction,
    track_pageview: false,
    persistence: LOCALSTORAGE,
  });

  return (userData: UserInfo) => {
    mixpanel.identify(userData.username);
    mixpanel.register({ loginSource: userData?.loginSource });

    const userProperties = getUserProperties(userData);
    mixpanel.people.set_once(userProperties);
  };
}

const trackPageView = (pageName: string) => {
  const isOnPremise = getIsOnpremise();
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;

  if (isOnPremise || !MIXPANEL_TOKEN || !isLoggedInUser()) {
    return;
  }

  try {
    mixpanel.track_pageview({ page: pageName });
    trackDuration(MIXPANEL_OTHER_EVENTS.PAGE_EXIT);
  } catch (e) {
    console.error("Mixpanel not initialized :", e);
  }
};

const trackEvent = (name: string, props?: Record<string, any>, duration?: number) => {
  const isOnPremise = getIsOnpremise();
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;

  // to be moved to other condition once on-prem build is ready
  trackCountlyEvent({
    key: name,
    segs: props,
    dur: duration,
  });
  if (isOnPremise || !isLoggedInUser()) {
    return;
  }

  if (!MIXPANEL_TOKEN) {
    return;
  }

  if (isEmpty(props)) {
    mixpanel.track(name);
    return;
  }

  try {
    mixpanel.track(name, props);
  } catch (e) {
    console.error("Mixpanel not initialized :", e);
  }
};

const trackPageExit = (page: string) => {
  const isOnPremise = getIsOnpremise();
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;

  if (isOnPremise || !MIXPANEL_TOKEN || !isLoggedInUser()) {
    return;
  }

  mixpanel.track(MIXPANEL_OTHER_EVENTS.PAGE_EXIT, { page });
};

const storeMixpanelEvents = (name: string, properties: Record<string, any> = {}) => {
  const events = localStorage.getItem(constants.PERSISTED.mixpanelEvents) || "[]";
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;
  if (events && MIXPANEL_TOKEN) {
    const eventsArray = JSON.parse(events);
    const _event = {
      name,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
      },
    };
    eventsArray.push(_event);
    localStorage.setItem(constants.PERSISTED.mixpanelEvents, JSON.stringify(eventsArray));
  }
};

const syncMixpanelEvents = () => {
  const events = localStorage.getItem(constants.PERSISTED.mixpanelEvents);
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;
  if (events && MIXPANEL_TOKEN) {
    const eventsArray = JSON.parse(events);
    eventsArray.forEach((event: Record<string, any>) => {
      mixpanel.track(event.name, event.properties);
    });
    localStorage.removeItem(constants.PERSISTED.mixpanelEvents);
  }
};

/* this function will track duration will target event is hit */
const trackDuration = (targetEvent: string) => {
  const isOnPremise = getIsOnpremise();
  const MIXPANEL_TOKEN = getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN") as string;

  if (isOnPremise || !MIXPANEL_TOKEN || !isLoggedInUser()) {
    return;
  }
  mixpanel.time_event(targetEvent);
};

function initializeCountly(userData: UserInfo) {
  const isOnPremise = getIsOnpremise();
  const APP_KEY = getEnv("NEXT_PUBLIC_COUNTLY_APP_KEY") as string;
  const COUNTLY_URL = `http://${window?.location.hostname}:${getEnv(
    "NEXT_PUBLIC_COUNTLY_PORT"
  )}`;

  // disabled countly for cloud builds and trial licensed users
  if (!isOnPremise || !APP_KEY || userData?.is_trial) {
    return () => {};
  }

  Countly.init({
    // server credentials
    app_key: APP_KEY,
    url: COUNTLY_URL,
    debug: !isProduction,
  });

  Countly.track_sessions();
  Countly.track_errors();
  Countly.q.push(["disable_heatmaps"]);
  return () => {
    let deviceId = userData?.username || userData?.details?.email || "";
    if (isMobile()) {
      deviceId = `${deviceId}_mobile`;
    }
    Countly.set_id(deviceId);
    const userProperties = getCountlyFormattedUserProperties(userData);
    trackCountlyUserProfile(userProperties);
    // if userData has the loginSource then add to countlyEvent
    if (!isEmpty(userData?.loginSource)) {
      trackCountlyEvent({
        key: MIXPANEL_EVENTS.LOGIN_SOURCE,
        segs: { loginSource: userData?.loginSource },
      });
    }
  };
}

const trackCountlyEvent = ({
  key,
  dur,
  segs,
}: {
  key: string;
  dur?: number;
  segs?: Record<string, any>;
}): void => {
  Countly.q.push([
    "add_event",
    {
      key,
      dur,
      segmentation: {
        ...segs,
        source_type: window.location.hostname.includes("localhost")
          ? "Qure Device"
          : "External Access",
      },
    },
  ]);
};

function trackCountlyUserProfile(userProfile: Record<string, any>) {
  Countly.q.push(["user_details", userProfile]);
}

/**
 * Track a page view event in Mixpanel with a page name parameter.
 * Kept for later use
 * @param pageName - The name of the page to track
 */
const trackCountlyPageView = (pageName: string, segments?: Record<string, any>) => {
  const isOnPremise = getIsOnpremise();

  if (!isOnPremise || !isLoggedInUser()) {
    return;
  }

  Countly.q.push(["track_pageview", pageName, [], segments]);
};

/**
 * React hook to track Countly view start and end for modals or components.
 * Kept for later use
 * @param viewName - The name of the view to track
 * @param active - Whether the view is active (open/mounted)
 */
export function useCountlyViewTracking(viewName: string, active: boolean) {
  useEffect(() => {
    if (active) {
      trackCountlyPageView(viewName);
      // return () => {
      //   trackCountlyPageView("")
      // };
    }
  }, [viewName, active]);
}

/**
 * Track qSync initiated event with location data
 * This function ensures the event is only tracked once per session
 * and handles location data retrieval with proper fallbacks
 */
const trackQsyncInitiated = async (): Promise<void> => {
  const qSyncEventLogged =
    localStorage.getItem(constants.PERSISTED.qSyncEventLogged) === "true";

  if (qSyncEventLogged) {
    return; // Event already logged for this session
  }

  // Mark as logged to prevent duplicate tracking
  localStorage.setItem(constants.PERSISTED.qSyncEventLogged, "true");

  try {
    // Import getLocationData dynamically to avoid circular dependencies
    const { getLocationData } = await import("./getLocationData");
    const locationData = await getLocationData();

    trackEvent(MIXPANEL_EVENTS.QSYNC_INITIATED, {
      geo_latitude: locationData.geo_latitude,
      geo_longitude: locationData.geo_longitude,
      location_permission: locationData.location_permission,
    });
  } catch (error) {
    console.warn("Failed to get location data for qSync tracking:", error);
    // Track without location data as fallback
    trackEvent(MIXPANEL_EVENTS.QSYNC_INITIATED, {
      geo_latitude: null,
      geo_longitude: null,
      location_permission: "unavailable",
    });
  }
};

export {
  initMixpanel,
  initializeCountly,
  trackPageView,
  trackCountlyPageView,
  trackEvent,
  trackPageExit,
  trackDuration,
  storeMixpanelEvents,
  syncMixpanelEvents,
  trackQsyncInitiated,
};
